import { get, postJSON, putJSON } from '../../../utils/Request';

export type EntitlementFeatureDescription = {
  title?: string;
  displayType?: string;
  supported?: boolean;
  quota?: number;
  unit?: string;
  period?: string;
  text?: string;
};

export type EntitlementPriceSummary = {
  regionCode?: string;
  currency?: string;
  amountMinor?: number;
  currencyFractionDigits?: number;
};

export type EntitlementSpecification = {
  displayName?: string;
  validityDays?: number | null;
  quotaLimit?: number;
  featureDescriptions?: EntitlementFeatureDescription[];
  prices?: EntitlementPriceSummary[];
};

export type EntitlementPackagePrice = {
  skuCode?: string;
  regionCode?: string;
  currency?: string;
  amountMinor?: number;
};

export type EntitlementSpecificationDetail = {
  specificationCode?: string;
  displayName?: string;
  validityDays?: number | null;
  /** 该规格售卖总次数。 */
  quotaLimit?: number;
  featureDescriptions?: EntitlementFeatureDescription[];
  effectiveFeatureCodes?: string[];
  featurePolicies?: Array<{
    featureNo?: string;
    policyType?: string;
    period?: string;
    quotaLimit?: number;
  }>;
  prices?: EntitlementPackagePrice[];
};

export type EntitlementPackageListItem = {
  packageCode?: string;
  displayName?: string;
  status?: string;
  /** 展示单位，如次、封。 */
  quotaUnit?: string;
  specifications?: EntitlementSpecification[];
};

/** GET /api/v1/entitlement-packages 的 content。 */
export type EntitlementPackageListPage = {
  records?: EntitlementPackageListItem[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

/** 分页查询独立权益包。上架与下架都会返回。返回体原样交给页面。 */
export function getEntitlementPackages(query: { pageNum: number; pageSize: number }) {
  return get<EntitlementPackageListPage>({
    path: '/api/v1/entitlement-packages',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize
    }
  });
}

export type CreateEntitlementPrice = {
  regionCode: string;
  currency: string;
  amountMinor?: number;
};

/** POST/PUT 规格：validityDays 空表示永久；quotaLimit 为正整数总次数。 */
export type CreateEntitlementSpecification = {
  validityDays?: number;
  quotaLimit: number;
  prices: CreateEntitlementPrice[];
};

/** POST /api/v1/entitlement-packages 请求体。修改时另传 expectedConfigHash。 */
export type CreateEntitlementPackageBody = {
  displayName: string;
  featureDescriptions: EntitlementFeatureDescription[];
  featureCode: string;
  /** 展示单位，如次、封；只用于展示。 */
  quotaUnit: string;
  specifications: CreateEntitlementSpecification[];
};

export type EntitlementPackageDetail = {
  packageCode?: string;
  displayName?: string;
  status?: string;
  permissionType?: string;
  groupCodes?: string[];
  featureCode?: string | null;
  /** 权益包统一展示文案。 */
  featureDescriptions?: EntitlementFeatureDescription[];
  /** 展示单位。 */
  quotaUnit?: string;
  specifications?: EntitlementSpecificationDetail[];
};

export type EntitlementPackageConfig = {
  data?: EntitlementPackageDetail;
  configHash?: string;
};

/** 查询独立权益包详情。content.data 为权益包，content.configHash 修改时原样传回。 */
export function getEntitlementPackageDetail(packageCode: string) {
  return get<EntitlementPackageConfig>({
    path: `/api/v1/entitlement-packages/${encodeURIComponent(packageCode)}`
  });
}

/** 上架独立权益包。expectedConfigHash 原样传回详情里的 configHash。配置变化时返回 409。 */
export function publishEntitlementPackage(packageCode: string, expectedConfigHash: string) {
  return postJSON<EntitlementPackageConfig>({
    path: `/api/v1/entitlement-packages/${encodeURIComponent(packageCode)}/publish`,
    params: { expectedConfigHash }
  });
}

export type GiftableEntitlementSpecification = {
  specificationCode?: string;
  quotaLimit?: number;
  validityDays?: number | null;
};

export type GiftableEntitlementPackage = {
  packageCode?: string;
  displayName?: string;
  featureDescriptions?: EntitlementFeatureDescription[];
  quotaUnit?: string;
  specifications?: GiftableEntitlementSpecification[];
};

/** GET /api/v1/entitlement-gift-orders/packages 的 content。 */
export type GiftableEntitlementPackagePage = {
  records?: GiftableEntitlementPackage[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

/** 分页查询可赠送独立权益规格。返回体原样交给页面。 */
export function getGiftableEntitlementPackages(query: { pageNum: number; pageSize: number }) {
  return get<GiftableEntitlementPackagePage>({
    path: '/api/v1/entitlement-gift-orders/packages',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize
    }
  });
}

export type CreateEntitlementGiftOrderBody = {
  userCode: string;
  packageCode: string;
  specificationCode: string;
  reason: string;
  idempotencyKey: string;
};

export type EntitlementGiftOrder = {
  orderNo?: string;
  orderStatus?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  amountMinor?: number;
  currency?: string;
  quoteExpiresAt?: { seconds?: number; nanos?: number } | string | null;
};

/** 赠送独立权益并生成订单。同一笔赠送重试时 idempotencyKey 保持不变。返回体原样交给页面。 */
export function createEntitlementGiftOrder(body: CreateEntitlementGiftOrderBody) {
  return postJSON<EntitlementGiftOrder>({
    path: '/api/v1/entitlement-gift-orders',
    params: body
  });
}

/** 新增独立权益包。不传 expectedConfigHash，编码由后端生成，默认下架。返回体原样交给页面。 */
export function createEntitlementPackage(body: CreateEntitlementPackageBody) {
  return postJSON<EntitlementPackageConfig>({
    path: '/api/v1/entitlement-packages',
    params: body
  });
}

export type UpdateEntitlementPackageBody = CreateEntitlementPackageBody & {
  expectedConfigHash: string;
};

/** 修改独立权益包。expectedConfigHash 原样传回详情里的 configHash。返回体原样交给页面。 */
export function updateEntitlementPackage(packageCode: string, body: UpdateEntitlementPackageBody) {
  return putJSON<EntitlementPackageConfig>({
    path: `/api/v1/entitlement-packages/${encodeURIComponent(packageCode)}`,
    params: body
  });
}

export type BenefitType = 'shop' | 'email';
export type BindMode = 'feature' | 'group';

export type BenefitPassPrice = {
  label: string;
  price: number;
  originalPrice: number;
  currency: string;
  region: string;
};

export type AdminBenefit = {
  id: string;
  name: string;
  type: BenefitType;
  bindMode: BindMode;
  groupId?: string;
  groupName?: string;
  featureId?: string;
  featureName?: string;
  passCards: BenefitPassPrice[];
  description: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type BenefitGiftRecord = {
  id: string;
  userName: string;
  benefitName: string;
  region: string;
  giftedBy: string;
  giftedAt: string;
  remark: string;
};

export type EntitlementGiftRecordQuery = {
  pageNum: number;
  pageSize: number;
  startTime?: string;
  endTime?: string;
  /** 赠送人名称，模糊匹配；去首尾空格，空白不传 */
  adminName?: string;
  /** 接收人名称，模糊匹配；去首尾空格，空白不传 */
  userName?: string;
  packageCode?: string;
};

export type EntitlementGiftRecord = {
  orderNo?: string;
  giftedAt?: string;
  adminCode?: string;
  adminName?: string;
  userCode?: string;
  userName?: string;
  packageCode?: string;
  packageName?: string;
  specificationCode?: string;
  featureCode?: string;
  quotaLimit?: number;
  quotaUnit?: string;
  validityDays?: number | null;
  startsAt?: string;
  expiresAt?: string | null;
  reason?: string;
  fulfillmentStatus?: string;
};

export type EntitlementGiftPage = {
  records?: EntitlementGiftRecord[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

function asGiftPageRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function parseEntitlementGiftPage(payload: unknown): {
  records: EntitlementGiftRecord[];
  total: number;
  pageNum: number;
  pageSize: number;
} {
  const root = asGiftPageRecord(payload);
  if (!root) {
    return { records: [], total: 0, pageNum: 1, pageSize: 10 };
  }
  const records = Array.isArray(root.records) ? (root.records as EntitlementGiftRecord[]) : [];
  const total = typeof root.total === 'number' && Number.isFinite(root.total) ? root.total : records.length;
  const pageNum = typeof root.pageNum === 'number' && Number.isFinite(root.pageNum) ? root.pageNum : 1;
  const pageSize = typeof root.pageSize === 'number' && Number.isFinite(root.pageSize) ? root.pageSize : 10;
  return { records, total, pageNum, pageSize };
}

/** 分页查询独立权益赠送记录。筛选条件走查询参数，空值不传。返回体原样交给页面。 */
export function queryEntitlementGiftOrders(query: EntitlementGiftRecordQuery) {
  const params: Record<string, string | number> = {
    pageNum: query.pageNum,
    pageSize: query.pageSize
  };
  const startTime = query.startTime?.trim();
  const endTime = query.endTime?.trim();
  const adminName = query.adminName?.trim();
  const userName = query.userName?.trim();
  const packageCode = query.packageCode?.trim();
  if (startTime) params.startTime = startTime;
  if (endTime) params.endTime = endTime;
  if (adminName) params.adminName = adminName;
  if (userName) params.userName = userName;
  if (packageCode) params.packageCode = packageCode;
  return get<EntitlementGiftPage>({
    path: '/api/v1/entitlement-gift-orders',
    params
  }).then((res) => {
    const page = res.code === 200 ? parseEntitlementGiftPage(res.content ?? res.data) : null;
    return {
      ...res,
      records: page?.records ?? [],
      total: page?.total ?? 0,
      pageNum: page?.pageNum ?? query.pageNum,
      pageSize: page?.pageSize ?? query.pageSize
    };
  });
}

export const BENEFIT_TYPE_LABEL: Record<BenefitType, string> = {
  shop: '店铺',
  email: '邮件'
};

export const BENEFIT_TYPE_OPTIONS = [
  { value: 'shop', label: '店铺' },
  { value: 'email', label: '邮件' }
] as const;

export const BIND_MODE_OPTIONS = [
  { value: 'feature', label: '按功能点' },
  { value: 'group', label: '按分组' }
] as const;

export const BENEFIT_GROUP_OPTIONS = [
  { value: 'g-shop', label: '店铺授权分组' },
  { value: 'g-email', label: '平台邮箱分组' },
  { value: 'g-invite', label: '邀约能力分组' }
];

export const BENEFIT_FEATURE_OPTIONS = [
  { value: 'f-shop-slot', label: '店铺槽位' },
  { value: 'f-email-quota', label: '邮件额度' },
  { value: 'f-export', label: '导出额度' },
  { value: 'f-detail-view', label: '详情页访问' }
];

export const adminBenefitList: AdminBenefit[] = [
  {
    id: 'bnf-1',
    name: '店铺槽位1个',
    type: 'shop',
    bindMode: 'feature',
    featureId: 'f-shop-slot',
    featureName: '店铺槽位',
    passCards: [
      {
        label: '1个店铺',
        price: 199,
        originalPrice: 299,
        currency: 'CNY',
        region: '中国大陆'
      },
      {
        label: '1个店铺',
        price: 680000,
        originalPrice: 890000,
        currency: 'VND',
        region: '越南'
      }
    ],
    description: '加购 1 个店铺授权席位，不改变套餐基础额度口径。',
    enabled: true,
    createdAt: '2026-04-12T10:20:00',
    updatedAt: '2026-09-18T15:40:11'
  },
  {
    id: 'bnf-2',
    name: '邮件额度 500 封',
    type: 'email',
    bindMode: 'group',
    groupId: 'g-email',
    groupName: '平台邮箱分组',
    passCards: [
      {
        label: '500封邮件',
        price: 99,
        originalPrice: 149,
        currency: 'CNY',
        region: '中国大陆'
      },
      {
        label: '500封邮件',
        price: 320000,
        originalPrice: 450000,
        currency: 'VND',
        region: '越南'
      }
    ],
    description: '当月追加 500 封平台邮件额度。',
    enabled: true,
    createdAt: '2026-05-03T09:12:33',
    updatedAt: '2026-10-02T11:08:45'
  },
  {
    id: 'bnf-3',
    name: '导出额度包',
    type: 'email',
    bindMode: 'feature',
    featureId: 'f-export',
    featureName: '导出额度',
    passCards: [
      {
        label: '1000次导出',
        price: 59,
        originalPrice: 89,
        currency: 'CNY',
        region: '中国大陆'
      }
    ],
    description: '追加导出次卡额度，按功能点绑定。',
    enabled: false,
    createdAt: '2026-06-21T14:05:19',
    updatedAt: '2026-08-30T08:22:01'
  }
];

export const benefitGiftRecordList: BenefitGiftRecord[] = [
  {
    id: 'bg-1',
    userName: 'VN-BeautyHub · 林晓',
    benefitName: '店铺槽位1个',
    region: '越南',
    giftedBy: '运营管理员',
    giftedAt: '2026-10-10 11:20',
    remark: '活动补偿'
  }
];
